// Fleet list (TanStack Table v8) over GET /fleet. Rendered standalone in tests
// (no router dep); the app passes onOpenSite to navigate into the drill-down.
import { Fragment, useMemo, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  flexRender,
  createColumnHelper,
  type ColumnDef,
  type FilterFn,
} from '@tanstack/react-table';
import { useFleet } from './useFleet';
import { useSyncFleet } from '../updates/hooks';
import { ConnectionBadge } from './ConnectionBadge';
import { SiteEnvironments } from './SiteEnvironments';
import { QueryState } from '../../components/QueryState';
import { formatInt, formatUsd, formatPlatform } from '../../lib/format';
import type { FleetResponse, FleetSite } from '../../lib/types';

export type FleetFilter = 'all' | 'updates' | 'disconnected';

export const FLEET_FILTERS: readonly FleetFilter[] = ['all', 'updates', 'disconnected'];

export function isFleetFilter(value: unknown): value is FleetFilter {
  return typeof value === 'string' && (FLEET_FILTERS as readonly string[]).includes(value);
}

const columnHelper = createColumnHelper<FleetSite>();

export interface FleetListProps {
  /** Navigate into a site drill-down (wired by the app; omitted in unit tests). */
  onOpenSite?: (siteId: string) => void;
  /** Active filter from the URL (?filter=). Defaults to 'all'. */
  filter?: FleetFilter;
  /** Navigate to another filter. Omitted → local fallback state (unit tests). */
  onFilterChange?: (filter: FleetFilter) => void;
}

export function FleetList({
  onOpenSite,
  filter: routeFilter = 'all',
  onFilterChange,
}: FleetListProps) {
  const { data, isPending, isError, error } = useFleet();
  // "Sync now" = GET /fleet/status fan-out (re-probes every site) + invalidate this listing.
  const sync = useSyncFleet();
  // Filter lives in the URL (?filter=) so it's linkable/refresh-safe; falls back
  // to local state when onFilterChange is omitted (unit tests).
  const [localFilter, setLocalFilter] = useState<FleetFilter>(routeFilter);
  const filter = onFilterChange ? routeFilter : localFilter;
  const setFilter = onFilterChange ?? setLocalFilter;

  const columns = useMemo<ColumnDef<FleetSite>[]>(
    () =>
      [
        columnHelper.accessor('domain', {
          header: 'Domain',
          cell: (info) => {
            const row = info.row.original;
            return onOpenSite ? (
              <button
                type="button"
                data-id="em-mgr--fleet--row--open"
                className="text-base font-medium text-blue-700 hover:underline dark:text-blue-400"
                onClick={() => onOpenSite(row.siteId)}
              >
                {row.domain}
              </button>
            ) : (
              <span className="text-base font-medium">{row.domain}</span>
            );
          },
        }),
        columnHelper.accessor('platform', {
          header: 'Platform',
          cell: (info) => formatPlatform(info.getValue()),
        }),
        columnHelper.accessor('coreVersion', {
          header: 'Core',
          cell: (info) => {
            const row = info.row.original;
            return (
              <span className="flex items-center gap-1">
                <span>{row.coreVersion}</span>
                {row.coreUpdateVersion && (
                  <span
                    data-id="em-mgr--fleet--core-update-badge"
                    className="rounded bg-blue-100 px-1.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/40 dark:text-blue-300"
                  >
                    → {row.coreUpdateVersion}
                  </span>
                )}
              </span>
            );
          },
        }),
        columnHelper.accessor('phpVersion', { header: 'PHP' }),
        columnHelper.accessor('updatesAvailable', {
          header: 'Updates',
          cell: (info) => {
            const n = info.getValue();
            return n > 0 ? (
              <span
                data-id="em-mgr--fleet--updates-count"
                className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
              >
                {n}
              </span>
            ) : (
              <span className="text-slate-400">0</span>
            );
          },
        }),
        columnHelper.accessor('connection', {
          header: 'Connection',
          cell: (info) => <ConnectionBadge state={info.getValue()} />,
        }),
        columnHelper.accessor('tokens30d', {
          // Includes cache-creation + cache-read tokens, which dominate a real turn (see note below).
          header: 'Tokens (30d, incl. cache)',
          cell: (info) => formatInt(info.getValue()),
        }),
        columnHelper.accessor('estCost30d', {
          header: 'Est cost (30d)',
          cell: (info) => formatUsd(info.getValue()),
        }),
      ] as ColumnDef<FleetSite>[],
    [onOpenSite],
  );

  const globalFilterFn: FilterFn<FleetSite> = useMemo(
    () => (row, _columnId, value: FleetFilter) => {
      const s = row.original;
      if (value === 'updates') return s.updatesAvailable > 0;
      if (value === 'disconnected') return s.connection !== 'connected';
      return true;
    },
    [],
  );

  const table = useReactTable({
    data: data?.sites ?? [],
    columns,
    state: { globalFilter: filter },
    globalFilterFn,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const rows = table.getRowModel().rows;

  return (
    <QueryState<FleetResponse>
      idBase="em-mgr--fleet"
      isPending={isPending}
      isError={isError}
      error={error}
      data={data}
    >
      {(fleetData) => (
    <section data-id="em-mgr--fleet--list" className="space-y-4">
      <div className="flex items-center gap-2">
        <h2 className="mr-auto text-lg font-semibold">Fleet</h2>
        <button
          type="button"
          data-id="em-mgr--fleet--sync"
          disabled={sync.isPending}
          onClick={() => sync.mutate()}
          className="rounded-md border border-slate-300 px-2 py-1 text-sm text-slate-700 hover:bg-slate-100 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
        >
          {sync.isPending ? 'Syncing…' : 'Sync now'}
        </button>
        <FilterButton id="em-mgr--fleet--filter-all" active={filter === 'all'} onClick={() => setFilter('all')}>
          All
        </FilterButton>
        <FilterButton
          id="em-mgr--fleet--filter-updates"
          active={filter === 'updates'}
          onClick={() => setFilter('updates')}
        >
          Updates available
        </FilterButton>
        <FilterButton
          id="em-mgr--fleet--filter-disconnected"
          active={filter === 'disconnected'}
          onClick={() => setFilter('disconnected')}
        >
          Disconnected
        </FilterButton>
      </div>

      {sync.isError && (
        <div data-id="em-mgr--fleet--sync-error" className="text-sm text-red-600 dark:text-red-400">
          Sync failed: {sync.error instanceof Error ? sync.error.message : 'Unknown error'}
        </div>
      )}

      {fleetData.sites.length === 0 ? (
        <div data-id="em-mgr--fleet--empty" className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-slate-500 dark:border-slate-700">
          No sites in the fleet yet.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-800">
          <table data-id="em-mgr--fleet--table" className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500 dark:bg-slate-900">
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((header) => (
                    <th key={header.id} className="px-3 py-2 font-medium">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {rows.map((row) => {
                const hasEnvs =
                  !!row.original.environments && row.original.environments.length > 0;
                return (
                  <Fragment key={row.id}>
                    {/* Site + its environments read as ONE group: divider above the
                        parent row only, no divider between parent and env-rows. */}
                    <tr
                      data-id="em-mgr--fleet--row"
                      className="border-t border-slate-200 dark:border-slate-700"
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className={`px-3 pt-2 ${hasEnvs ? 'pb-1' : 'pb-2'}`}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                    {hasEnvs && (
                      <tr data-id="em-mgr--fleet--env-row">
                        <td colSpan={row.getVisibleCells().length} className="px-3 pb-2 pt-0">
                          <SiteEnvironments environments={row.original.environments} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p data-id="em-mgr--fleet--tokens-note" className="text-xs text-slate-500 dark:text-slate-400">
        Tokens (30d) counts input + output + cache-creation + cache-read tokens. Cache tokens
        dominate a typical turn, so this total is far larger than input + output alone.
      </p>
    </section>
      )}
    </QueryState>
  );
}

function FilterButton({
  id,
  active,
  onClick,
  children,
}: {
  id: string;
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      data-id={id}
      onClick={onClick}
      className={`rounded-md border px-2 py-1 text-sm ${
        active
          ? 'border-slate-900 bg-slate-900 text-white dark:border-slate-100 dark:bg-slate-100 dark:text-slate-900'
          : 'border-slate-300 text-slate-700 dark:border-slate-700 dark:text-slate-300'
      }`}
    >
      {children}
    </button>
  );
}
